import React, { useState } from "react";
import toast from "react-hot-toast";
import useAttendance from "../../hooks/useAttendance";
import useAttendanceScreenRules from "../../hooks/useAttendanceScreenRules";
import TimingCard from "./TimingCard";
import AttendanceCalendar from './AttendanceCalendar';
import ActivityLog from "./ActivityLog";
import ReasonModal from "./ReasonModal";
import WorkLogModal from "./WorkLogModal";

import { OverviewSkeleton } from "../Skeletons";

const Overview = () => {
  const {
    formatTime,
    formatDuration,
    state,
    totalBreakTime,
    trackedTime,
    clockIn,
    startBreak,
    endBreak,
    clockOut,
    addWorkLog,
    activityLog,
    loading,
    timingSynced,
  } = useAttendance();

  const {
    shouldBlockAttendanceOnMobile,
  } = useAttendanceScreenRules();

  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [workLogModalOpen, setWorkLogModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState(null); // 'break' or 'clockout'

  // User Profile
  const user = JSON.parse(localStorage.getItem("currentUser")) || {
    firstName: "Nevine",
    lastName: "Acotanza",
    role: { name: "UI/UX Designer" },
  };

  const showMobileBlockedMessage = () => {
    toast.error(
      "You are working from office and you are not allowed to clock in, clock out, break in, break out from mobile."
    );
  };

  // Handlers
  const handleClockToggle = () => {
    if (shouldBlockAttendanceOnMobile) {
      showMobileBlockedMessage();
      return;
    }

    if (state.isClockedIn) {
      // Trying to clock out
      // Check 8 hours logic (trackedTime is in seconds)
      const hours = trackedTime / 3600;

      if (hours < 8) {
        setModalMode("clockout");
        setReasonModalOpen(true);
      } else {
        clockOut();
      }
    } else {
      // If continuing or new session
      clockIn();
    }
  };

  const handleBreakToggle = () => {
    if (shouldBlockAttendanceOnMobile) {
      showMobileBlockedMessage();
      return;
    }

    if (state.isOnBreak) {
      endBreak();
    } else {
      setModalMode("break");
      setReasonModalOpen(true);
    }
  };

  const handleReasonSubmit = (reason) => {
    if (modalMode === "break") {
      startBreak(reason);
    } else if (modalMode === "clockout") {
      clockOut(reason);
    }
    setReasonModalOpen(false);
  };

  if (loading && !state.liveTracking) {
    return <OverviewSkeleton />;
  }

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 flex flex-col gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-indigo-200 text-center relative overflow-hidden group hover:shadow-md transition-all duration-300">
              <div className="absolute top-0 left-0 w-full h-24 bg-indigo-50 z-0" />
              <div className="relative z-10 pt-2">
                <div className="w-24 h-24 rounded-full border-4 border-white shadow-md mx-auto mb-4 overflow-hidden ring-2 ring-indigo-100">
                  <img
                    src="/profile-placeholder.png"
                    alt="Profile"
                    className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-105"
                    onError={(e) => {
                      e.target.src =
                        "https://ui-avatars.com/api/?name=" +
                        user.firstName +
                        "+" +
                        user.lastName +
                        "&background=c7d2fe&color=312e81&size=128";
                    }}
                  />
                </div>
                <h2 className="text-xl font-bold text-indigo-800 tracking-tight">
                  {user.firstName} {user.lastName}
                </h2>
                <div className="mt-3">
                  <p className="text-xs font-semibold text-indigo-700 bg-indigo-50 inline-block px-4 py-1.5 rounded-md border border-indigo-100 uppercase tracking-wide">
                    {user.role?.name || "Employee"}
                  </p>
                </div>
              </div>
            </div>

            <TimingCard
              trackedTime={trackedTime}
              formatTime={formatTime}
              formatDuration={formatDuration}
              isOnBreak={state.isOnBreak}
              isClockedIn={state.isClockedIn}
              totalBreakTime={totalBreakTime}
              onClockInClick={handleClockToggle}
              onBreakClick={handleBreakToggle}
              isSyncing={!timingSynced}
            />
          </div>

          <AttendanceCalendar />
        </div>

        <div className="lg:col-span-1">
          <ActivityLog logs={activityLog} />
        </div>
      </div>

      {/* Modals */}
      <ReasonModal
        isOpen={reasonModalOpen}
        onClose={() => setReasonModalOpen(false)}
        onSubmit={handleReasonSubmit}
        title={
          modalMode === "break" ? "Break Reason" : "Early Clock Out Reason"
        }
        description={
          modalMode === "clockout"
            ? "Your working hours is less than 08:00:00 hour please give reason."
            : "Please provide a reason for taking a break."
        }
        submitLabel={modalMode === "clockout" ? "Clock Out" : "Start Break"}
      />

      <WorkLogModal
        isOpen={workLogModalOpen}
        onClose={() => setWorkLogModalOpen(false)}
        onSubmit={addWorkLog}
      />
    </>
  );
};

export default Overview;
